import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Package, Upload, CheckCircle, LogOut, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { logError } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardSimple = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [adminToken, setAdminToken] = useState('');
  const [uploadingPackage, setUploadingPackage] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setAdminToken(token);
    loadDashboardData(token);
  }, [navigate]);

  const loadDashboardData = async (token) => {
    setIsLoading(true);
    try {
      // Load stats
      const statsRes = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Load bookings
      const response = await fetch(`${API_URL}/api/admin/bookings?status=scheduled`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Fetch carrier locations for each booking
        const bookingsWithLocations = await Promise.all(
          data.map(async (booking) => {
            try {
              const locRes = await fetch(
                `${API_URL}/api/admin/bookings/${booking.id}/carrier-locations`,
                { headers: { 'Authorization': `Bearer ${token}` } }
              );
              if (locRes.ok) {
                const locData = await locRes.json();
                return { ...booking, locations: locData };
              }
            } catch (err) {
              logError('Failed to load locations:', err);
            }
            return booking;
          })
        );
        setBookings(bookingsWithLocations);
      }
    } catch (error) {
      logError('Error loading bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (bookingId, packageNumber, event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingPackage(`${bookingId}-${packageNumber}`);
    
    try {
      // In production, you'd upload to cloud storage (S3, Cloudinary, etc.)
      // For now, we'll convert to base64 as a placeholder
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        
        // Upload confirmation label
        const response = await fetch(
          `${API_URL}/api/admin/bookings/${bookingId}/packages/${packageNumber}/upload-confirmation`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${adminToken}`
            },
            body: JSON.stringify({
              confirmation_label_url: base64Image
            })
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          alert(result.email_sent 
            ? '✅ Confirmation uploaded & customer notified!' 
            : '✅ Confirmation uploaded!'
          );
          loadDashboardData(adminToken);
        } else {
          alert('❌ Failed to upload confirmation');
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      logError('Upload error:', error);
      alert('❌ Upload failed');
    } finally {
      setUploadingPackage(null);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/admin/login');
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading pickups...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <nav style={{ 
        backgroundColor: '#1f2937', 
        color: 'white', 
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>🚚 ReturnPal Admin</h1>
        <Button onClick={handleLogout} variant="outline" style={{ color: 'white', borderColor: 'white' }}>
          <LogOut size={16} style={{ marginRight: '0.5rem' }} />
          Logout
        </Button>
      </nav>

      {/* Main Content */}
      <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* Stats Overview */}
        {stats && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <Card>
              <CardContent style={{ padding: '1.5rem' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>Total Revenue</p>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '2rem', fontWeight: '700', color: '#10b981' }}>
                  ${stats.total_revenue?.toFixed(2) || '0.00'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent style={{ padding: '1.5rem' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>Total Bookings</p>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '2rem', fontWeight: '700', color: '#3b82f6' }}>
                  {stats.total_bookings || 0}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent style={{ padding: '1.5rem' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>Active Subscriptions</p>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '2rem', fontWeight: '700', color: '#8b5cf6' }}>
                  {stats.active_subscriptions || 0}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent style={{ padding: '1.5rem' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>In Progress</p>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '2rem', fontWeight: '700', color: '#f59e0b' }}>
                  {stats.in_progress_bookings || 0}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent style={{ padding: '1.5rem' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>Total Customers</p>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '2rem', fontWeight: '700', color: '#ec4899' }}>
                  {stats.total_customers || 0}
                </p>
              </CardContent>
            </Card>
          </div>
        )}
        
        <h2 style={{ fontSize: '1.75rem', marginBottom: '1.5rem', color: '#111827' }}>
          📦 Today's Pickups ({bookings.length})
        </h2>

        {bookings.length === 0 ? (
          <Card>
            <CardContent style={{ padding: '3rem', textAlign: 'center' }}>
              <Package size={64} style={{ color: '#9ca3af', margin: '0 auto 1rem' }} />
              <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>No scheduled pickups</p>
            </CardContent>
          </Card>
        ) : (
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {bookings.map((booking) => (
              <Card key={booking.id} style={{ border: '2px solid #e5e7eb' }}>
                <CardHeader style={{ backgroundColor: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <CardTitle style={{ fontSize: '1.25rem', color: '#111827' }}>
                        #{booking.confirmation_number}
                      </CardTitle>
                      <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
                        <Clock size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                        {booking.pickup_date} • {booking.pickup_window}
                      </p>
                    </div>
                    <div style={{ 
                      padding: '0.5rem 1rem', 
                      backgroundColor: '#fef3c7', 
                      borderRadius: '6px',
                      fontWeight: '600',
                      color: '#92400e'
                    }}>
                      {booking.number_of_packages} Packages
                    </div>
                  </div>
                </CardHeader>

                <CardContent style={{ padding: '1.5rem' }}>
                  {/* Pickup Location - PROMINENT */}
                  <div style={{ 
                    backgroundColor: '#dbeafe', 
                    padding: '1.25rem', 
                    borderRadius: '8px',
                    marginBottom: '1.5rem',
                    border: '2px solid #3b82f6'
                  }}>
                    <h3 style={{ 
                      margin: '0 0 0.75rem 0', 
                      fontSize: '1.1rem', 
                      color: '#1e40af',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <MapPin size={20} style={{ marginRight: '0.5rem' }} />
                      PICKUP LOCATION
                    </h3>
                    <p style={{ margin: '0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#111827' }}>
                      {booking.locations?.pickup_location?.customer || booking.user_name}
                    </p>
                    <p style={{ margin: '0.25rem 0', color: '#374151' }}>
                      📍 {booking.locations?.pickup_location?.address || booking.location_address || booking.location_name}
                    </p>
                    <p style={{ margin: '0.25rem 0', color: '#374151' }}>
                      <Phone size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                      {booking.locations?.pickup_location?.phone || booking.user_phone}
                    </p>
                    <p style={{ margin: '0.25rem 0', color: '#374151' }}>
                      <Mail size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                      {booking.user_email}
                    </p>
                  </div>

                  {/* Packages & Drop-off Locations */}
                  <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#374151' }}>
                    📦 Packages & Drop-off Locations:
                  </h4>
                  
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {booking.packages?.map((pkg, idx) => {
                      const carrierLocation = booking.locations?.carrier_locations?.[pkg.carrier];
                      const isDelivered = pkg.status === 'delivered';
                      
                      return (
                        <div 
                          key={idx}
                          style={{ 
                            padding: '1rem', 
                            backgroundColor: isDelivered ? '#f0fdf4' : '#ffffff',
                            border: isDelivered ? '2px solid #10b981' : '1px solid #e5e7eb',
                            borderRadius: '8px'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ flex: 1 }}>
                              <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600', color: '#111827' }}>
                                Package {pkg.package_number}: {pkg.item_description}
                              </p>
                              
                              <div style={{ 
                                backgroundColor: '#fef2f2', 
                                padding: '0.75rem', 
                                borderRadius: '6px',
                                marginBottom: '0.5rem'
                              }}>
                                <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#991b1b' }}>
                                  🚚 DROP OFF AT: {pkg.carrier}
                                </p>
                                {carrierLocation ? (
                                  <>
                                    <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#7f1d1d' }}>
                                      <strong>{carrierLocation.name}</strong>
                                    </p>
                                    <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#7f1d1d' }}>
                                      📍 {carrierLocation.address}
                                    </p>
                                    <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#7f1d1d' }}>
                                      📞 {carrierLocation.phone}
                                    </p>
                                    <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#7f1d1d' }}>
                                      🕒 {carrierLocation.hours}
                                    </p>
                                    {carrierLocation.notes && (
                                      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem', fontStyle: 'italic', color: '#991b1b' }}>
                                        💡 {carrierLocation.notes}
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#7f1d1d' }}>
                                    Drop off at nearest {pkg.carrier} location
                                  </p>
                                )}
                              </div>

                              {pkg.package_type === 'label' && (
                                <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#6b7280' }}>
                                  📋 Tracking: ***{pkg.tracking_last6}
                                </p>
                              )}
                              {pkg.package_type === 'qr' && (
                                <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#6b7280' }}>
                                  📱 ReturnPal ID: {pkg.returnpal_qr_id || 'Will be generated'}
                                </p>
                              )}
                            </div>

                            <div style={{ marginLeft: '1rem' }}>
                              {isDelivered ? (
                                <div style={{ textAlign: 'center' }}>
                                  <CheckCircle size={32} style={{ color: '#10b981' }} />
                                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem', color: '#059669', fontWeight: '600' }}>
                                    DELIVERED
                                  </p>
                                </div>
                              ) : (
                                <div>
                                  <label htmlFor={`upload-${booking.id}-${pkg.package_number}`}>
                                    <Button
                                      as="span"
                                      size="sm"
                                      style={{ cursor: 'pointer', backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}
                                      disabled={uploadingPackage === `${booking.id}-${pkg.package_number}`}
                                    >
                                      <Upload size={16} style={{ marginRight: '0.5rem' }} />
                                      Upload Proof
                                    </Button>
                                  </label>
                                  <input
                                    id={`upload-${booking.id}-${pkg.package_number}`}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    style={{ display: 'none' }}
                                    onChange={(e) => handleImageUpload(booking.id, pkg.package_number, e)}
                                  />
                                  <p style={{ 
                                    margin: '0.5rem 0 0 0', 
                                    fontSize: '0.7rem', 
                                    color: '#6b7280',
                                    textAlign: 'center',
                                    maxWidth: '120px'
                                  }}>
                                    Upload receipt after drop-off
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardSimple;
